import { formatCurrencyAmount } from '@/utils/currency.utils';

import { formatPaymentDate } from '../channelPresentation';
import { resolvePaymentCategory } from '../paymentCategories';

import { findArrayAtPaths, normalizeChannelId, PAYMENT_DATA_PATHS } from './sourcePaths';
import { coerceDate, formatStatus, resolveAmountValue, resolveCurrency } from './valueResolvers';

export function resolveSupportPaymentDescription(entry) {
  const recurringCandidate =
    entry?.isRecurring ??
    entry?.is_recurring ??
    entry?.recurring;
  const isRecurring = Boolean(recurringCandidate);

  const supportedUserName =
    entry?.supported_user_name ??
    entry?.supported_artist_name ??
    entry?.artist_name ??
    null;

  if (typeof supportedUserName === 'string' && supportedUserName.trim()) {
    return `${isRecurring ? 'Monthly Support for' : 'One-time Support for'} ${supportedUserName.trim()}`;
  }

  return isRecurring ? 'Monthly Support' : 'One-time Support';
}

export function resolvePaymentDescription(entry) {
  if (!entry) {
    return 'Payment';
  }

  const typeCandidate = (entry.type ?? entry.payment_type ?? entry.order_type ?? '').toString().trim().toLowerCase();
  if (typeCandidate === 'support') {
    return resolveSupportPaymentDescription(entry);
  }

  if (typeCandidate === 'original' || typeCandidate === 'originals') {
    return 'Original';
  }

  if (typeCandidate === 'limited_edition' || typeCandidate === 'limited edition' || typeCandidate === 'limited-edition') {
    return 'Limited Edition';
  }

  if (typeCandidate === 'channel' || typeCandidate === 'channel_subscription') {
    const ownerUsername =
      entry.owner_username
      ?? entry.ownerUsername
      ?? entry.channel?.owner_username
      ?? entry.channel?.owner?.user_username
      ?? entry.seller_username
      ?? entry.sellerUsername
      ?? null;
    if (typeof ownerUsername === 'string' && ownerUsername.trim()) {
      return `Channel Subscription to @${ownerUsername.trim().replace(/^@/, '')}`;
    }
    return 'Channel Subscription';
  }

  const candidates = [
    entry.description,
    entry.note,
    entry.details,
    entry.title,
    entry.label,
    entry.payment_description,
    entry.support_title,
    entry.metadata?.description,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      const trimmed = candidate.trim();
      const lowerTrimmed = trimmed.toLowerCase();
      if (lowerTrimmed === 'original' || lowerTrimmed === 'originals') {
        return 'Original';
      }
      if (lowerTrimmed === 'limited_edition' || lowerTrimmed === 'limited edition' || lowerTrimmed === 'limited-edition') {
        return 'Limited Edition';
      }
      return trimmed;
    }
  }
  if (entry.supported_user_name || entry.artist_name) {
    return resolveSupportPaymentDescription(entry);
  }
  return 'Payment';
}

export function normalizePaymentEntry(entry, index) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const typeCandidate = (entry.type ?? entry.payment_type ?? entry.order_type ?? '').toString().trim().toLowerCase();
  const productModel = entry.product_model
    ?? entry.productModel
    ?? entry.holding_model
    ?? entry.holdingModel
    ?? entry.metadata?.product_model
    ?? null;
  const isChannelSubscription = typeCandidate === 'channel' || typeCandidate === 'channel_subscription';
  const channelId = normalizeChannelId(
    entry.channelId
    ?? entry.channel_id
    ?? entry.product
    ?? entry.product_id
    ?? entry.metadata?.channel
    ?? entry.metadata?.product
  );

  const amountValue = resolveAmountValue(entry);
  const currencyCode = resolveCurrency(entry);
  const date =
    coerceDate(entry.paymentDate ?? entry.payment_date) ??
    coerceDate(entry.paidAt ?? entry.paid_at) ??
    coerceDate(entry.transactionDate ?? entry.transaction_date) ??
    coerceDate(entry.createdAt ?? entry.created_at) ??
    coerceDate(entry.date) ??
    null;
  const description = resolvePaymentDescription(entry);
  const status = formatStatus(entry, Boolean(entry?.isPaid ?? entry?.is_active ?? entry?.status === 'paid'));
  const cancellationDate =
    coerceDate(
      entry.cancelled_at
      ?? entry.canceled_at
      ?? entry.cancelledAt
      ?? entry.canceledAt
      ?? entry.cancellation_date
      ?? entry.cancellationDate
      ?? entry.cancel_at
      ?? entry.cancelAt
      ?? null
    ) ?? null;
  const cancellationReasonRaw =
    entry.cancel_reason
    ?? entry.cancelReason
    ?? entry.cancel_details
    ?? entry.cancelDetails
    ?? entry.cancellation_reason
    ?? entry.cancellationReason
    ?? null;
  const cancellationReason = typeof cancellationReasonRaw === 'string'
    ? cancellationReasonRaw.trim()
    : null;
  const amountLabel = Number.isFinite(amountValue)
    ? formatCurrencyAmount(amountValue, currencyCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : null;

  return {
    id: entry.id ?? entry._id ?? entry.paymentId ?? `payment-${index}`,
    description,
    amountLabel,
    amountValue: Number.isFinite(amountValue) ? amountValue : null,
    status,
    date,
    dateLabel: formatPaymentDate(date),
    type: typeCandidate || 'payment',
    productModel,
    orderId: normalizeChannelId(entry.order_id ?? entry.orderId ?? null),
    orderNumber:
      typeof (entry.order_number ?? entry.orderNumber) === 'string'
        ? (entry.order_number ?? entry.orderNumber).trim()
        : (entry.order_number ?? entry.orderNumber ?? null),
    orderStatus: entry.order_status ?? entry.status ?? null,
    shippingStatus: entry.order_shipping_address?.shipping_status ?? entry.shipping_status ?? null,
    orderPayment: entry.order_payment ?? entry.orderPayment ?? null,
    orderShippingAddress: entry.order_shipping_address ?? entry.shipping_address ?? null,
    orderLimitedEditionData: entry.order_limited_edition_data ?? entry.orderLimitedEditionData ?? null,
    billingAddress: entry.order_billing_address ?? entry.billing_address ?? null,
    sellerId: entry.order_seller ?? entry.seller_id ?? null,
    sellerEmail: entry.seller_email ?? null,
    sellerUsername: entry.seller_username ?? null,
    billingInfo: entry.order_billing_info ?? entry.billing_info ?? null,
    paymentStatus: entry.order_payment_status ?? entry.payment_status ?? null,
    cancellationDate,
    cancellationReason: cancellationReason || null,
    invoiceUrl: entry.invoice_url ?? null,
    invoiceAvailable: Boolean(entry.invoice_available ?? entry.invoice_url),
    invoiceBillingRequired: Boolean(entry.invoice_billing_required),
    isChannelSubscription,
    channelId,
    category: resolvePaymentCategory({
      type: typeCandidate,
      description,
      productModel,
      isChannelSubscription,
    }),
  };
}

export function extractPaymentEntries(profileData) {
  if (!profileData) {
    return [];
  }
  const raw = findArrayAtPaths(profileData, PAYMENT_DATA_PATHS);
  if (raw.length === 0) {
    return [];
  }
  return raw
    .map(normalizePaymentEntry)
    .filter(Boolean)
    .sort((a, b) => {
      if (a.date && b.date) {
        return b.date.getTime() - a.date.getTime();
      }
      if (a.date) {
        return -1;
      }
      if (b.date) {
        return 1;
      }
      return 0;
    });
}

export function buildChannelSubscriptionPaymentEntries(subscriptions) {
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
    return [];
  }

  return subscriptions
    .filter((subscription) => subscription?.isActive)
    .map((subscription, index) => {
      const date = subscription.nextBillingDate ?? subscription.joinedAt ?? null;
      const ownerUsername = typeof subscription?.ownerUsername === 'string'
        ? subscription.ownerUsername.trim().replace(/^@/, '')
        : null;
      return {
        id: `channel-sub-payment-${subscription.channelId ?? index}`,
        description: ownerUsername
          ? `Channel Subscription to @${ownerUsername}`
          : 'Channel Subscription',
        amountLabel: subscription?.amountLabel ?? 'Amount unavailable',
        status: 'Active',
        date,
        dateLabel: formatPaymentDate(date),
        isChannelSubscription: true,
        channelId: subscription?.channelId ?? null,
        category: 'Channels',
      };
    });
}
